import { jwtDecode } from "jwt-decode";

/**
 * id_token の claims 読み取り。
 *
 * 署名検証はしない（backend が検証済みのトークンを cookie に入れている）。
 * ここでの用途は表示名の取得と、期限切れの早期検知だけ。
 */

export type IdTokenClaims = {
  readonly sub?: string;
  /** 発行者。username のフォールバック（`sub@iss`）に使う。 */
  readonly iss?: string;
  readonly exp?: number;
  readonly email?: string;
  readonly name?: string;
  readonly given_name?: string;
  readonly preferred_username?: string;
  readonly groups?: readonly string[];
};

export type AuthUser = {
  /** IdP 上の一意な識別子。SpiffArena の username に対応。 */
  readonly username: string;
  readonly email: string | null;
  readonly displayName: string;
};

export function decodeIdToken(idToken: string): IdTokenClaims | null {
  try {
    return jwtDecode<IdTokenClaims>(idToken);
  } catch {
    return null;
  }
}

/** exp が無い場合も期限切れ扱いにする（安全側に倒す）。 */
export function isExpired(claims: IdTokenClaims, nowMs: number): boolean {
  if (claims.exp === undefined) return true;
  return claims.exp * 1000 <= nowMs;
}

/**
 * backend（`authorization_service.py`）と**同じ規則**で username を組む。
 * preferred_username → email → `sub@iss`。
 *
 * ここがずれると「自分が起票した案件」の絞り込み（`process_initiator_username`）が
 * 一致せず、一覧が黙って 0 件になる。
 */
function toUsername(claims: IdTokenClaims): string {
  const preferred = firstNonEmpty(claims.preferred_username, claims.email);
  if (preferred !== undefined) return preferred;

  const sub = firstNonEmpty(claims.sub);
  const iss = firstNonEmpty(claims.iss);
  if (sub !== undefined && iss !== undefined) return `${sub}@${iss}`;

  return sub ?? "";
}

export function toAuthUser(claims: IdTokenClaims): AuthUser {
  const username = toUsername(claims);

  return {
    username,
    email: firstNonEmpty(claims.email) ?? null,
    displayName:
      firstNonEmpty(claims.name, claims.given_name, username) ?? username,
  };
}

function firstNonEmpty(
  ...values: readonly (string | undefined)[]
): string | undefined {
  return values.find((value) => value !== undefined && value !== "");
}
